import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputLeftElement,
  InputGroup,
  Tooltip,
  Flex,
} from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import * as _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

import { getSingleErrorMessageFromFieldErrors } from 'packages/common-frontend/components/FormField';

type DateTimeInputFormFieldProps<K> = {
  name: K;
  type?: 'date' | 'datetime-local';
  label: string;
  placeholder?: string;
  defaultValue?: string;
};

export const DateTimeInputFormField = <
  T extends { [key: string]: string },
  K extends string & keyof T
>({
  name,
  type = 'date',
  label,
  placeholder = '',
  defaultValue = '',
}: DateTimeInputFormFieldProps<K>) => {
  const formik = useFormikContext<T>();

  return (
    <FormControl isInvalid={!!formik.errors[name]}>
      <FormLabel>{label}</FormLabel>
      <Input
        id={name}
        name={name}
        type={type}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        placeholder={placeholder}
        defaultValue={defaultValue}
      />
      {formik.errors[name] && (
        <FormErrorMessage>
          {getSingleErrorMessageFromFieldErrors(formik.errors[name])}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

// Helper functions for currency input form field
const reverse = (str: string) => str.split('').reverse().join('');

const formatCurrency = (value: string | undefined): string => {
  // Only format valid currency with a one decimal or no decimals
  if (value && value.replace(/[^.]/g, '').length <= 1) {
    let dollars;
    let cents;
    if (_.includes(value, '.')) {
      [dollars, cents] = value.split('.');
    } else {
      dollars = value;
      cents = '';
    }
    const dollarSections = reverse(dollars).match(/.{1,3}/g);
    let formattedDollars = '';
    if (dollarSections) {
      formattedDollars = reverse(dollarSections.join(','));
    }

    if (cents || _.includes(value, '.')) {
      return `${formattedDollars}.${cents}`;
    }

    return `${formattedDollars}`;
  }

  return value || '';
};

type CurrencyInputFormFieldProps<K> = {
  name: K;
  type?: 'USD';
  label: string;
  placeholder?: string;
  tooltip?: string;
};

export const CurrencyInputFormField = <
  T extends { [key: string]: string },
  K extends string & keyof T
>({
  name,
  type = 'USD',
  label,
  placeholder = '',
  tooltip,
}: CurrencyInputFormFieldProps<K>) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const oldInput = useRef('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [cursorOffset, setCursorOffset] = useState(0);
  const [counter, setCounter] = useState(0); // Used to force effect and ensure cursor position is correct

  useEffect(() => {
    if (inputRef.current) {
      const newCursorPosition = cursorPosition + cursorOffset;
      inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
    }
  }, [cursorPosition, cursorOffset, counter]);

  const formik = useFormikContext<T>();
  let PlaceholderIcon;
  if (type === 'USD') {
    PlaceholderIcon = (
      <InputLeftElement
        pointerEvents="none"
        color="gray.300"
        fontSize="1.2em"
        height="100%"
      >
        $
      </InputLeftElement>
    );
  }

  const onChange = (event: any) => {
    const oldValue = oldInput.current;
    const value = event.target.value as string;
    const newCursorPosition = inputRef.current?.selectionStart || 0;
    setCounter(counter + 1);
    // Exit early if invalid characters or more than one decimal
    const invalidCharacterCount = (value.match(/[^\d.,]/g) || []).length;
    const decimalCount = (value.match(/[.]/g) || []).length;
    const fractional = value.split('.').at(1) ?? '';
    const decimalPlaces = fractional.replace(/[^\d]/g, '').length;

    if (invalidCharacterCount > 0 || decimalCount > 1 || decimalPlaces > 4) {
      // Avoid moving cursor for invalid character movement
      setCursorPosition(newCursorPosition - 1);
      setCursorOffset(0);
      return;
    }

    oldInput.current = value;
    const formattedOldValue = oldValue.replace(/[^\d.]/g, '');
    const formattedValue = value.replace(/[^\d.]/g, '');
    const parts = formattedValue.split('.');
    if (parts.length > 2) {
      formik.setFieldError(
        name,
        'Invalid currency format - only one decimal allowed.',
      );
    } else {
      formik.setFieldError(name, undefined);
    }

    const formattedDisplayOldValue = formatCurrency(formattedOldValue);
    const formattedDisplayValue = formatCurrency(formattedValue);

    // check change in characters preceding this string index before/after value change

    const inputChange = formattedValue.length - formattedOldValue.length;
    const oldOffset = (
      formattedDisplayOldValue
        .slice(0, newCursorPosition - inputChange)
        .match(/,/g) || []
    ).length;
    const newOffset = (
      formattedDisplayValue.slice(0, newCursorPosition).match(/,/g) || []
    ).length;
    const newCursorOffset = newOffset - oldOffset;

    setCursorPosition(newCursorPosition);
    setCursorOffset(newCursorOffset);

    formik.setFieldValue(name, formattedValue === '' ? null : formattedValue);
  };

  const formValue = formatCurrency(_.get(formik.values, name));
  const formError = _.get(formik.errors, name);

  return (
    <FormControl isInvalid={!!formError}>
      <Flex>
        <FormLabel>{label}</FormLabel>
        {tooltip && (
          <Tooltip label={tooltip} placement="right">
            <InfoOutlineIcon marginTop="4px" />
          </Tooltip>
        )}
      </Flex>
      <InputGroup>
        {PlaceholderIcon}
        <Input
          ref={inputRef}
          id={name}
          name={name}
          type="text"
          value={formValue || ''}
          onChange={onChange}
          placeholder={placeholder}
        />
      </InputGroup>
      {formError && (
        <FormErrorMessage>
          {getSingleErrorMessageFromFieldErrors(formError)}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};
